import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const table = tv({
  variants: {
    color: {
      default: {
        // Darker header like the screenshot
        th: "bg-secondary",
        // Subtle separators + hover
        tr: `
          border-b border-border/40 bg-background-2 transition-colors
          last:border-b-0
          hover:bg-secondary/40
        `,
      },
      secondary: {
        th: "bg-secondary",
      },
      // full-grid document look used by article/report markdown
      document: {
        table: "rounded-none border border-border",
        th: "h-auto border border-border bg-background-2 py-2",
        td: "border border-border py-2",
        tr: "",
      },
    },
    size: {
      sm: {},
      base: {
        th: "text-base",
        td: "text-base",
      },
    },
    outline: {
      true: {
        th: `
          border-r border-border/40
          last:border-r-0
        `,
        td: `
          border-r border-b border-border/40
          last:border-r-0
        `,
        tr: `
          last:[&>td:first-child]:rounded-bl-lg
          last:[&>td:last-child]:rounded-br-lg
        `,
      },
    },
    bordered: {
      true: {
        table: "border-separate border-spacing-0 border border-border/50",
        td: `
          border-separate border-spacing-0 border-b border-border/40
          [tbody>tr:last-child>&]:border-b-0
        `,
        th: `border-separate border-spacing-0 border-b border-border/60`,
      },
    },
    sticky: {
      true: {
        th: "sticky top-0 z-10",
      },
    },
    roundedBottom: {
      true: {
        table: "rounded-b-lg",
      },
      false: {
        table: "rounded-b-none",
      },
    },
  },
  slots: {
    th: `
      h-12 border-b border-border/60 px-4 text-left text-sm font-semibold
      text-text-primary
    `,
    tr: ``,
    td: `bg-inherit px-4 py-3 align-middle text-sm text-text-primary`,
    thead: ``,
    table: "min-w-full table-fixed border-collapse overflow-hidden rounded-lg",
  },
  defaultVariants: {
    color: "default",
    size: "sm",
    roundedBottom: true,
  },
});

type TableVariants = VariantProps<typeof table>;

const PlainTableContext = createContext<TableVariants | undefined>(undefined);

function usePlainTableContext() {
  return useContext(PlainTableContext);
}

export type PlainTableProps = {
  readonly className?: string;
  readonly children: ReactNode;
  readonly ref?: React.Ref<HTMLTableElement>;
} & TableVariants;

export type PlainTableHeaderProps = {
  readonly children: ReactNode;
};

export type PlainTableBodyProps = {
  readonly children: ReactNode;
};

export type PlainTableRowProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly isDragging?: boolean;
  readonly onDragOver?: React.DragEventHandler;
  readonly onDrop?: React.DragEventHandler;
};

export type PlainTableCellProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly colSpan?: number;
  readonly style?: React.CSSProperties;
  readonly isAction?: boolean;
};

export type PlainTableHeaderCellProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly colSpan?: number;
  readonly style?: React.CSSProperties;
};

export function PlainTable({
  children,
  className,
  ref,
  ...variants
}: PlainTableProps) {
  const { table: tableClass } = table(variants);

  return (
    <PlainTableContext.Provider value={variants}>
      <table ref={ref} className={tableClass({ className })}>
        {children}
      </table>
    </PlainTableContext.Provider>
  );
}

export function PlainTableHeader({ children }: PlainTableHeaderProps) {
  const variants = usePlainTableContext();
  const { thead } = table(variants);
  return <thead className={thead()}>{children}</thead>;
}

export function PlainTableBody({ children }: PlainTableBodyProps) {
  return <tbody>{children}</tbody>;
}

export function PlainTableRow({
  children,
  className,
  style,
  isDragging,
  onDragOver,
  onDrop,
}: PlainTableRowProps) {
  const variants = usePlainTableContext();
  const { tr } = table(variants);
  return (
    <tr
      className={tr({ className })}
      style={style}
      data-dragging={isDragging}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {children}
    </tr>
  );
}

export function PlainTableCell({
  children,
  className,
  colSpan,
  style,
  isAction,
}: PlainTableCellProps) {
  const variants = usePlainTableContext();
  const { td } = table(variants);
  return (
    <td
      className={td({ className })}
      colSpan={colSpan}
      style={style}
      data-action={isAction}
    >
      {children}
    </td>
  );
}

export function PlainTableHeaderCell({
  children,
  className,
  colSpan,
  style,
}: PlainTableHeaderCellProps) {
  const variants = usePlainTableContext();
  const { th } = table(variants);
  return (
    <th className={th({ className })} colSpan={colSpan} style={style}>
      {children}
    </th>
  );
}
